import type { PuttPathSample } from "./greenVectors";

export type PuttSlopeProfileV3 = {
  thetaStartDeg: number;
  thetaHoleDeg: number;
  thetaMaxBreakDeg: number;
};

export type PuttSideOfFallLine =
  | "LEFT_OF_FALL_LINE"
  | "RIGHT_OF_FALL_LINE"
  | "ON_FALL_LINE";

export type PuttRelationToFallLine =
  | "ALONG"
  | "DIAGONAL"
  | "ACROSS";

export type GrassType = "bent" | "bermuda" | "poa" | "other";

export type GreenMetadata = {
  courseId: string;
  hole: number;

  // Green-level drainage / orientation if known
  fallLineDirectionDeg?: number;

  // Regional / site-wide background tilt
  mtbDirectionDeg: number;
  mtbStrengthDeg: number;

  // Optional course/green context
  stimpDefault?: number;
  grassType?: GrassType;
};

export type GreenReadInputsV3 = {
  distanceFt: number;

  slopes: PuttSlopeProfileV3;

  // Defined from the golfer’s perspective standing behind the ball
  // and looking toward the hole.
  sideOfFallLine: PuttSideOfFallLine;
  relationToFallLine: PuttRelationToFallLine;

  // Optional if ball/pin image tapping or directional geometry is known.
  puttDirectionDeg?: number;

  // Optional override; otherwise use green.stimpDefault or fallback.
  stimp?: number;

  // Intended capture pace / rollout behavior.
  rolloutFt: number;

  green: GreenMetadata;
};

export type GreenReadResultV3 = {
  breakInches: number;

  breakDirection: "LEFT_TO_RIGHT" | "RIGHT_TO_LEFT" | "STRAIGHT";

  aimSideOfCup: "LEFT" | "RIGHT" | "CENTER";
  aimOffsetInches: number;

  speedPastCupInches: number;
  speedInstruction: string;

  readInstruction: string;
  aimInstruction: string;

  effectiveSlopeDeg: number;
  thetaEffBase: number;
  thetaCross: number;
  thetaGrade: number;
  thetaMTBCross: number;
  mtbContribution: number;

  lengthFactor: number;
  speedFactor: number;
  gradeFactor: number;
  rolloutFactor: number;

  derivedAngleToFallLineDeg: number;
  derivedAngleToMTBDeg: number;

  rawBreakInches: number;

  // Rough (not precise) categorical read of the grade component, using
  // the classic "above/below the hole" framing golfers already think in.
  aboveOrBelowHole: "above" | "below" | "level";
  gradeSeverity: "flat" | "slight" | "moderate" | "significant";
  gradeDescription: string;

  // Set when the start-of-putt and near-hole slope samples disagree on
  // break direction — a rough signal that this is a double-breaker the
  // single aim-point/break-inches number won't fully capture.
  multiBreakNote: string | null;

  // Which engine produced this read. Absent (undefined) for callers that
  // pre-date this field. "path" = computePuttBreakFromPath (per-point
  // vector data along the ball→pin line); "manual" = computePuttBreakV3
  // (hand-entered fall-line/side/height inputs).
  readMethod?: "path" | "manual";
  // Number of path samples that had usable direction data, when readMethod
  // is "path" — surfaced in the UI so it's clear the read came from real
  // green data rather than a single blended guess.
  pathSamplesUsed?: number;
};

const degToRad = (deg: number) => (deg * Math.PI) / 180;

const clamp = (min: number, value: number, max: number) =>
  Math.max(min, Math.min(value, max));

const safeNum = (value: unknown, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const normalizeSignedDeg = (deg: number) => {
  return (((deg + 180) % 360) + 360) % 360 - 180;
};

/**
 * Maps golfer-friendly fall-line relationship inputs into a usable angle
 * between putt direction and fall line.
 *
 * Interpretation:
 * - ALONG    => mostly up/down the fall line
 * - DIAGONAL => halfway between along and across
 * - ACROSS   => mostly across the fall line
 *
 * `sideOfFallLine` is preserved separately for directionality semantics.
 */
function deriveAngleToFallLineDeg(
  relationToFallLine: PuttRelationToFallLine
): number {
  if (relationToFallLine === "ALONG") return 0;
  if (relationToFallLine === "DIAGONAL") return 45;
  return 90; // ACROSS
}

/**
 * Computes the angle between putt direction and MTB direction if putt
 * direction is known. Otherwise returns 0 as a neutral fallback.
 */
function deriveAngleToMTBDeg(
  puttDirectionDeg: number | undefined,
  mtbDirectionDeg: number
): number {
  if (!Number.isFinite(puttDirectionDeg)) return 0;
  return normalizeSignedDeg((puttDirectionDeg as number) - mtbDirectionDeg);
}

/**
 * Converts side-of-fall-line and the effective cross-slope sign into
 * golfer-facing break direction.
 *
 * This is a placeholder convention and may need tuning against real reads.
 */
function deriveBreakDirection(
  effectiveSlopeDeg: number,
  sideOfFallLine: PuttSideOfFallLine,
  breakInches: number,
  straightThresholdInches = 1.0
): "LEFT_TO_RIGHT" | "RIGHT_TO_LEFT" | "STRAIGHT" {
  if (breakInches < straightThresholdInches || Math.abs(effectiveSlopeDeg) < 0.05) {
    return "STRAIGHT";
  }

  // Placeholder directional convention:
  // positive effective cross-slope = one break direction,
  // negative = the other. Side-of-fall-line may be used later
  // to refine sign conventions if needed after field testing.
  if (effectiveSlopeDeg > 0) return "LEFT_TO_RIGHT";
  return "RIGHT_TO_LEFT";
}

function deriveAimSideOfCup(
  breakDirection: "LEFT_TO_RIGHT" | "RIGHT_TO_LEFT" | "STRAIGHT"
): "LEFT" | "RIGHT" | "CENTER" {
  if (breakDirection === "STRAIGHT") return "CENTER";
  if (breakDirection === "LEFT_TO_RIGHT") return "LEFT";
  return "RIGHT";
}

function deriveSpeedPastCupInches(thetaGrade: number): number {
  if (thetaGrade >= 1.5) return 12;
  if (thetaGrade >= 0.5) return 8;
  if (thetaGrade <= -1.5) return -8;
  if (thetaGrade <= -0.5) return -4;
  return 0;
}

export function computePuttBreakV3(
  input: GreenReadInputsV3
): GreenReadResultV3 {
  const D = Math.max(0, safeNum(input.distanceFt));
  const S = safeNum(input.stimp, safeNum(input.green.stimpDefault, 10));
  const R = Math.max(0.25, safeNum(input.rolloutFt, 1.5));

  const thetaStartDeg = safeNum(input.slopes?.thetaStartDeg);
  const thetaHoleDeg = safeNum(input.slopes?.thetaHoleDeg);
  const thetaMaxBreakDeg = safeNum(input.slopes?.thetaMaxBreakDeg);

  const derivedAngleToFallLineDeg = deriveAngleToFallLineDeg(
    input.relationToFallLine
  );

  const derivedAngleToMTBDeg = deriveAngleToMTBDeg(
    input.puttDirectionDeg,
    input.green.mtbDirectionDeg
  );

  const psiRad = degToRad(derivedAngleToFallLineDeg);
  const betaRad = degToRad(derivedAngleToMTBDeg);

  // 1) Local slope weighting:
  // start + hole + max-break region
  const thetaEffBase =
    0.25 * thetaStartDeg +
    0.25 * thetaHoleDeg +
    0.50 * thetaMaxBreakDeg;

  // 2) Resolve local slope into cross-slope and grade.
  // Sign is determined by side of fall line:
  //   RIGHT → ball breaks left  (negative = RIGHT_TO_LEFT)
  //   LEFT  → ball breaks right (positive = LEFT_TO_RIGHT)
  //   ON    → no lateral component (straight / grade-only)
  const fallLineSideSign =
    input.sideOfFallLine === "LEFT_OF_FALL_LINE" ? 1 :
    input.sideOfFallLine === "RIGHT_OF_FALL_LINE" ? -1 :
    0;

  const thetaCross = thetaEffBase * Math.sin(psiRad) * fallLineSideSign;
  const thetaGrade = thetaEffBase * Math.cos(psiRad);

  // 3) MTB contribution as a background bias
  const thetaMTBCross = safeNum(input.green.mtbStrengthDeg) * Math.sin(betaRad);

  const wMTB =
    (1 / (1 + Math.pow(thetaEffBase / 1.75, 2))) *
    (1 - Math.exp(-D / 8));

  const mtbContribution = wMTB * thetaMTBCross;

  // 4) Net effective cross-slope
  const thetaEff = thetaCross + mtbContribution;

  // 5) Length factor
  let lengthFactor = 1;
  if (D <= 10) lengthFactor = 0.9;
  else if (D > 20) lengthFactor = 1.15;

  // 6) Speed/grade/rollout factors
  const speedFactor = 1 + 0.06 * (S - 10);
  const gradeFactor = clamp(0.7, 1 + 0.18 * thetaGrade, 1.35);
  const rolloutFactor = clamp(0.82, Math.pow(1.5 / R, 0.35), 1.18);

  // 7) Calibrated break model
  const K = 0.28;

  const rawBreakInches =
    K *
    Math.pow(Math.max(D, 0), 1.35) *
    Math.abs(thetaEff) *
    speedFactor *
    gradeFactor *
    rolloutFactor *
    lengthFactor;

  const breakInches = clamp(0, rawBreakInches, 180);

  const breakDirection = deriveBreakDirection(
    thetaEff,
    input.sideOfFallLine,
    breakInches
  );

  const aimSideOfCup = deriveAimSideOfCup(breakDirection);
  const aimOffsetInches = breakDirection === "STRAIGHT" ? 0 : breakInches;

  const speedPastCupInches = deriveSpeedPastCupInches(thetaGrade);

  let speedInstruction = "Die at the cup";
  if (speedPastCupInches > 0) {
    speedInstruction = `Roll it about ${Math.abs(speedPastCupInches)} inches past the cup`;
  } else if (speedPastCupInches < 0) {
    speedInstruction = `Die it about ${Math.abs(speedPastCupInches)} inches short of the cup`;
  }

  let readInstruction = "Straight putt";
  if (breakDirection === "LEFT_TO_RIGHT") {
    readInstruction = `${breakInches.toFixed(1)} inches left-to-right break`;
  } else if (breakDirection === "RIGHT_TO_LEFT") {
    readInstruction = `${breakInches.toFixed(1)} inches right-to-left break`;
  }

  let aimInstruction = "Aim center cup";
  if (aimSideOfCup === "LEFT") {
    aimInstruction = `Aim ${aimOffsetInches.toFixed(1)} inches left of the cup`;
  } else if (aimSideOfCup === "RIGHT") {
    aimInstruction = `Aim ${aimOffsetInches.toFixed(1)} inches right of the cup`;
  }

  // Rough above/below-hole severity, using the same thetaGrade already
  // driving speedPastCupInches — just labeled the way golfers think about
  // it ("I'm below the hole" / "I'm above the hole") rather than a raw
  // degree number. Not meant to be precise, just directionally useful.
  const gradeMagnitude = Math.abs(thetaGrade);
  const GRADE_DEADZONE = 0.15;

  let aboveOrBelowHole: "above" | "below" | "level" = "level";
  if (thetaGrade > GRADE_DEADZONE) aboveOrBelowHole = "below";
  else if (thetaGrade < -GRADE_DEADZONE) aboveOrBelowHole = "above";

  let gradeSeverity: "flat" | "slight" | "moderate" | "significant" = "flat";
  if (gradeMagnitude >= 1.8) gradeSeverity = "significant";
  else if (gradeMagnitude >= 0.8) gradeSeverity = "moderate";
  else if (gradeMagnitude >= GRADE_DEADZONE) gradeSeverity = "slight";

  let gradeDescription = "Flat — no meaningful uphill/downhill";
  if (aboveOrBelowHole !== "level") {
    const upDown = aboveOrBelowHole === "below" ? "uphill" : "downhill";
    const severityLabel = gradeSeverity.charAt(0).toUpperCase() + gradeSeverity.slice(1);
    gradeDescription = `${severityLabel} ${upDown} — you're ${aboveOrBelowHole} the hole`;
  }

  // Rough double-breaker detection: does the slope near the ball push the
  // opposite way from the slope near the hole? Uses the raw start/apex
  // samples independently rather than the blended thetaEffBase, so a
  // reversal in the middle of the putt doesn't get averaged away.
  const crossStart = thetaStartDeg * Math.sin(psiRad) * fallLineSideSign;
  const crossApex = thetaMaxBreakDeg * Math.sin(psiRad) * fallLineSideSign;
  const MIN_SEGMENT_CROSS_DEG = 0.4;

  let multiBreakNote: string | null = null;
  if (
    Math.abs(crossStart) >= MIN_SEGMENT_CROSS_DEG &&
    Math.abs(crossApex) >= MIN_SEGMENT_CROSS_DEG &&
    Math.sign(crossStart) !== Math.sign(crossApex)
  ) {
    const startDir = crossStart > 0 ? "left-to-right" : "right-to-left";
    const apexDir = crossApex > 0 ? "left-to-right" : "right-to-left";
    multiBreakNote = `Breaks ${startDir} early, then ${apexDir} near the hole — double-breaker, treat the single aim point above as a rough starting line only`;
  }

  return {
    breakInches,

    breakDirection,

    aimSideOfCup,
    aimOffsetInches,

    speedPastCupInches,
    speedInstruction,

    readInstruction,
    aimInstruction,

    aboveOrBelowHole,
    gradeSeverity,
    gradeDescription,
    multiBreakNote,

    effectiveSlopeDeg: thetaEff,
    thetaEffBase,
    thetaCross,
    thetaGrade,
    thetaMTBCross,
    mtbContribution,

    lengthFactor,
    speedFactor,
    gradeFactor,
    rolloutFactor,

    derivedAngleToFallLineDeg,
    derivedAngleToMTBDeg,

    rawBreakInches,

    readMethod: "manual",
  };
}

// ---------------------------------------------------------------------------
// Path-based read — AimPoint-style
// ---------------------------------------------------------------------------
//
// Rather than blending the green down to one fall-line angle chosen by
// hand, this walks several points along the actual ball→pin line and uses
// each one's own locally-read slope direction and magnitude (see
// `sampleVectorPath` in greenVectors.ts — this is the "read the arrows
// along the line, then the heat map for where it's steepest" approach).
// A green with two opposing slopes shows up as a real sign reversal
// between samples instead of being averaged into a single number; only
// available where a course has vectorized green data (see the manual
// computePuttBreakV3 above for the fallback).

export type PuttPathInput = {
  distanceFt: number;
  path: PuttPathSample[];
  // Bearing of the ball→pin line, same convention as PuttPathSample.directionDeg.
  puttLineBearingDeg: number;
  stimp?: number;
  rolloutFt: number;
  green: GreenMetadata;
};

export function computePuttBreakFromPath(
  input: PuttPathInput
): GreenReadResultV3 {
  const D = Math.max(0, safeNum(input.distanceFt));
  const S = safeNum(input.stimp, safeNum(input.green.stimpDefault, 10));
  const R = Math.max(0.25, safeNum(input.rolloutFt, 1.5));

  const usable = (input.path ?? []).filter(
    (p) => p.directionDeg != null && Number.isFinite(p.slopeDeg)
  );

  type Comp = { t: number; cross: number; grade: number; weight: number };

  const comps: Comp[] = usable.map((p) => {
    const relRad = degToRad(
      normalizeSignedDeg(p.directionDeg! - input.puttLineBearingDeg)
    );
    // cross = component pushing the ball off the ball→pin line (break);
    // grade = component along the line, in the direction of travel.
    //
    // directionDeg is the downhill (fall-line) direction at that point — the
    // way a resting ball would roll — not the uphill direction. Verified
    // against a precise ball/pin tap on Waccabuc hole 1: the arrows there
    // point ~155-160° away from the ball→pin travel bearing at every sample
    // along the line, and the putt is a confirmed real uphill putt (ball
    // below the hole). So when the downhill direction points opposite the
    // direction of travel (relRad near 180°, cos negative), that's uphill —
    // hence the negation below, so grade > 0 consistently means "below the
    // hole / uphill" to match aboveOrBelowHole's convention.
    const cross = p.slopeDeg * Math.sin(relRad);
    const grade = -p.slopeDeg * Math.cos(relRad);

    // Weight by the sample's own steepness — the steepest spot on the heat
    // map should dominate the read, not get averaged with flatter ground —
    // and by proximity to the hole, since a putt is most slope-sensitive in
    // its last few feet where it's rolling slowest.
    const magnitudeWeight = Math.max(p.slopeDeg, 0.05);
    const proximityWeight = 0.5 + 0.5 * p.t;
    return { t: p.t, cross, grade, weight: magnitudeWeight * proximityWeight };
  });

  const weightSum = comps.reduce((s, c) => s + c.weight, 0);
  const thetaCross =
    weightSum > 0
      ? comps.reduce((s, c) => s + c.cross * c.weight, 0) / weightSum
      : 0;
  const thetaGrade =
    weightSum > 0
      ? comps.reduce((s, c) => s + c.grade * c.weight, 0) / weightSum
      : 0;

  const thetaEff = thetaCross;

  let lengthFactor = 1;
  if (D <= 10) lengthFactor = 0.9;
  else if (D > 20) lengthFactor = 1.15;

  const speedFactor = 1 + 0.06 * (S - 10);
  const gradeFactor = clamp(0.7, 1 + 0.18 * thetaGrade, 1.35);
  const rolloutFactor = clamp(0.82, Math.pow(1.5 / R, 0.35), 1.18);

  // Same calibrated break model as computePuttBreakV3 — this function only
  // changes how the geometry (thetaCross/thetaGrade) is derived.
  const K = 0.28;
  const rawBreakInches =
    K *
    Math.pow(Math.max(D, 0), 1.35) *
    Math.abs(thetaEff) *
    speedFactor *
    gradeFactor *
    rolloutFactor *
    lengthFactor;

  const breakInches = clamp(0, rawBreakInches, 180);

  // sideOfFallLine is unused inside deriveBreakDirection (see its comment
  // above) — the sign of thetaEff, derived here from real per-point
  // direction, already fully determines break direction.
  const breakDirection = deriveBreakDirection(
    thetaEff,
    "ON_FALL_LINE",
    breakInches
  );

  const aimSideOfCup = deriveAimSideOfCup(breakDirection);
  const aimOffsetInches = breakDirection === "STRAIGHT" ? 0 : breakInches;
  const speedPastCupInches = deriveSpeedPastCupInches(thetaGrade);

  let speedInstruction = "Die at the cup";
  if (speedPastCupInches > 0) {
    speedInstruction = `Roll it about ${Math.abs(speedPastCupInches)} inches past the cup`;
  } else if (speedPastCupInches < 0) {
    speedInstruction = `Die it about ${Math.abs(speedPastCupInches)} inches short of the cup`;
  }

  let readInstruction = "Straight putt";
  if (breakDirection === "LEFT_TO_RIGHT") {
    readInstruction = `${breakInches.toFixed(1)} inches left-to-right break`;
  } else if (breakDirection === "RIGHT_TO_LEFT") {
    readInstruction = `${breakInches.toFixed(1)} inches right-to-left break`;
  }

  let aimInstruction = "Aim center cup";
  if (aimSideOfCup === "LEFT") {
    aimInstruction = `Aim ${aimOffsetInches.toFixed(1)} inches left of the cup`;
  } else if (aimSideOfCup === "RIGHT") {
    aimInstruction = `Aim ${aimOffsetInches.toFixed(1)} inches right of the cup`;
  }

  const gradeMagnitude = Math.abs(thetaGrade);
  const GRADE_DEADZONE = 0.15;

  let aboveOrBelowHole: "above" | "below" | "level" = "level";
  if (thetaGrade > GRADE_DEADZONE) aboveOrBelowHole = "below";
  else if (thetaGrade < -GRADE_DEADZONE) aboveOrBelowHole = "above";

  let gradeSeverity: "flat" | "slight" | "moderate" | "significant" = "flat";
  if (gradeMagnitude >= 1.8) gradeSeverity = "significant";
  else if (gradeMagnitude >= 0.8) gradeSeverity = "moderate";
  else if (gradeMagnitude >= GRADE_DEADZONE) gradeSeverity = "slight";

  let gradeDescription = "Flat — no meaningful uphill/downhill";
  if (aboveOrBelowHole !== "level") {
    const upDown = aboveOrBelowHole === "below" ? "uphill" : "downhill";
    const severityLabel =
      gradeSeverity.charAt(0).toUpperCase() + gradeSeverity.slice(1);
    gradeDescription = `${severityLabel} ${upDown} — you're ${aboveOrBelowHole} the hole`;
  }

  // Multiple fall lines: compare the putt's first half against its second
  // half using each half's OWN samples — not a blended average — so a real
  // reversal partway down the green surfaces instead of being smoothed away.
  const firstHalf = comps.filter((c) => c.t < 0.5);
  const secondHalf = comps.filter((c) => c.t >= 0.5);
  const halfAvgCross = (arr: Comp[]) =>
    arr.length ? arr.reduce((s, c) => s + c.cross, 0) / arr.length : 0;
  const crossStart = halfAvgCross(firstHalf);
  const crossApex = halfAvgCross(secondHalf);
  const MIN_SEGMENT_CROSS_DEG = 0.4;

  let multiBreakNote: string | null = null;
  if (
    Math.abs(crossStart) >= MIN_SEGMENT_CROSS_DEG &&
    Math.abs(crossApex) >= MIN_SEGMENT_CROSS_DEG &&
    Math.sign(crossStart) !== Math.sign(crossApex)
  ) {
    const startDir = crossStart > 0 ? "left-to-right" : "right-to-left";
    const apexDir = crossApex > 0 ? "left-to-right" : "right-to-left";
    multiBreakNote = `Breaks ${startDir} early, then ${apexDir} near the hole — double-breaker, treat the single aim point above as a rough starting line only`;
  }

  return {
    breakInches,

    breakDirection,

    aimSideOfCup,
    aimOffsetInches,

    speedPastCupInches,
    speedInstruction,

    readInstruction,
    aimInstruction,

    aboveOrBelowHole,
    gradeSeverity,
    gradeDescription,
    multiBreakNote,

    effectiveSlopeDeg: thetaEff,
    thetaEffBase: thetaEff,
    thetaCross,
    thetaGrade,
    thetaMTBCross: 0,
    mtbContribution: 0,

    lengthFactor,
    speedFactor,
    gradeFactor,
    rolloutFactor,

    derivedAngleToFallLineDeg: 0,
    derivedAngleToMTBDeg: 0,

    rawBreakInches,

    readMethod: "path",
    pathSamplesUsed: usable.length,
  };
}